import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Zap, Mail, Phone, MapPin, Clock } from "lucide-react";
import { PROGRAMS } from "@/lib/programs";

export default function Footer() {
    return (
        <footer
            data-testid="site-footer"
            className="relative border-t border-zinc-900 bg-black text-zinc-200"
        >
            <div className="h-1 stripe-accent opacity-80" />
            <div className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-20 grid md:grid-cols-12 gap-10">
                <div className="md:col-span-5 space-y-5" id="visit">
                    <Link to="/" className="inline-flex items-center gap-2.5">
                        <span className="inline-flex items-center justify-center w-9 h-9 bg-brand-red text-white">
                            <Zap className="w-4 h-4" strokeWidth={2.5} />
                        </span>
                        <span className="font-display text-2xl tracking-wider text-white">
                            MAXIMUM SPORTS CONDITIONING
                        </span>
                    </Link>
                    <p className="text-sm leading-relaxed max-w-md">
                        Athletic development for every age and every level. We start as young as 8 to
                        establish a foundation, and train every age above that — because everyone is an
                        athlete for life.
                    </p>
                    <div className="space-y-2 text-sm">
                        <a
                            href="https://www.google.com/maps/search/?api=1&query=13415+SE+30th+St+Bellevue+WA+98005"
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="footer-address-link"
                            className="flex items-start gap-2 hover:text-white transition-colors"
                        >
                            <MapPin className="w-4 h-4 text-brand-red flex-shrink-0 mt-0.5" />
                            <span>
                                13415 SE 30th St, Bellevue, WA 98005
                                <span className="block text-xs text-zinc-400 mt-0.5">First building on the left in the complex</span>
                            </span>
                        </a>
                        <a
                            href="mailto:sachinm@maxsc.net"
                            data-testid="footer-email-link"
                            className="flex items-center gap-2 hover:text-white transition-colors"
                        >
                            <Mail className="w-4 h-4 text-brand-red" />
                            <span>sachinm@maxsc.net</span>
                        </a>
                        <a
                            href="tel:+14256414310"
                            data-testid="footer-phone-link"
                            className="flex items-center gap-2 hover:text-white transition-colors"
                        >
                            <Phone className="w-4 h-4 text-brand-red" />
                            <span>425-641-4310</span>
                        </a>
                        <div className="flex items-start gap-2 pt-1" data-testid="footer-hours">
                            <Clock className="w-4 h-4 text-brand-red flex-shrink-0 mt-0.5" />
                            <div className="space-y-0.5">
                                <div>Mon – Fri · 6:30 AM – 12 PM · 3 PM – 7:30 PM</div>
                                <div>Saturday · 10 AM – 1 PM</div>
                                <div className="text-xs text-zinc-400">Sunday — by demand</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="md:col-span-3">
                    <div className="text-[10px] tracking-[0.25em] text-zinc-300 uppercase mb-4">
                        Programs
                    </div>
                    <ul className="space-y-3">
                        {PROGRAMS.map((p) => (
                            <li key={p.slug}>
                                <Link
                                    to={p.path}
                                    data-testid={`footer-program-${p.slug}`}
                                    className="text-sm hover:text-brand-red transition-colors"
                                >
                                    {p.title}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>

                <div className="md:col-span-2">
                    <div className="text-[10px] tracking-[0.25em] text-zinc-300 uppercase mb-4">
                        Site
                    </div>
                    <ul className="space-y-3">
                        <li>
                            <Link to="/" className="text-sm hover:text-brand-red transition-colors">
                                Home
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/policy/membership"
                                data-testid="footer-pricing-policy"
                                className="text-sm hover:text-brand-red transition-colors"
                            >
                                Pricing &amp; Policy
                            </Link>
                        </li>
                        <li>
                            <Link
                                to="/legal/refund-policy"
                                data-testid="footer-refund-policy"
                                className="text-sm hover:text-brand-red transition-colors"
                            >
                                Refund &amp; Cancellation
                            </Link>
                        </li>
                    </ul>
                </div>

                <div className="md:col-span-2">
                    <div className="text-[10px] tracking-[0.25em] text-zinc-300 uppercase mb-4">
                        Follow
                    </div>
                    <div className="flex gap-3">
                        <a
                            href="#"
                            data-testid="footer-instagram"
                            className="inline-flex items-center justify-center w-10 h-10 border border-zinc-800 text-white hover:border-brand-red hover:text-brand-red transition-colors"
                            aria-label="Instagram"
                        >
                            <Instagram className="w-4 h-4" />
                        </a>
                        <a
                            href="#"
                            data-testid="footer-facebook"
                            className="inline-flex items-center justify-center w-10 h-10 border border-zinc-800 text-white hover:border-brand-red hover:text-brand-red transition-colors"
                            aria-label="Facebook"
                        >
                            <Facebook className="w-4 h-4" />
                        </a>
                        <a
                            href="#"
                            data-testid="footer-youtube"
                            className="inline-flex items-center justify-center w-10 h-10 border border-zinc-800 text-white hover:border-brand-red hover:text-brand-red transition-colors"
                            aria-label="YouTube"
                        >
                            <Youtube className="w-4 h-4" />
                        </a>
                    </div>
                </div>
            </div>

            <div className="border-t border-zinc-900">
                <div className="max-w-7xl mx-auto px-6 md:px-10 py-6 flex flex-col md:flex-row gap-3 items-start md:items-center justify-between text-xs text-zinc-300">
                    <div>© {new Date().getFullYear()} Maximum Sports Conditioning. All rights reserved.</div>
                    <div className="tracking-[0.2em] uppercase">Train. Compete. Outwork.</div>
                </div>
            </div>
        </footer>
    );
}
